import { Form } from 'react-bootstrap';

import { pushCharacterCount, pushLengthState } from '../utils';

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  maxLength: number;
  // Above this the text is likely cut on a lock screen (a warning, never refused).
  warnLength?: number | null;
  multiline?: boolean;
  disabled?: boolean;
  placeholder?: string;
};

const COUNTER_CLASS = { ok: 'text-muted', warn: 'text-warning font-weight-bold', over: 'text-danger font-weight-bold' };

/**
 * A text input with its character count against the server's limits: the warning threshold (lock-screen cut) and
 * the hard limit are shown apart. Counting only: what is refused is the server's call, reported on save.
 */
export function PushTextField({
  id,
  label,
  value,
  onChange,
  maxLength,
  warnLength = null,
  multiline = false,
  disabled = false,
  placeholder,
}: Props): JSX.Element {
  const length = pushCharacterCount(value);
  const state = pushLengthState(length, warnLength, maxLength);
  // Shared by both controls; `as` is a literal in each, so the compiler knows `rows` applies to the textarea.
  const controlProps = { value, placeholder, disabled, isInvalid: state === 'over' };
  return (
    <Form.Group controlId={id} className="mb-2">
      <div className="d-flex justify-content-between align-items-end">
        <Form.Label className="mb-1">{label}</Form.Label>
        <small className={COUNTER_CLASS[state]}>
          {length} / {maxLength}
          {warnLength != null ? ` · lock screen cuts after ${warnLength}` : ''}
          {state === 'over' ? ' · over the limit' : ''}
          {state === 'warn' ? ' · likely cut' : ''}
        </small>
      </div>
      {multiline ? (
        <Form.Control as="textarea" rows={3} {...controlProps} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <Form.Control as="input" {...controlProps} onChange={(event) => onChange(event.target.value)} />
      )}
    </Form.Group>
  );
}
